import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  setThankYouNote,
  confirmOrder,
  openDownloadModal,
  updateDeliveryNotes,
  updateUnit
} from './actions';
import { isScheduledOrder } from 'shared/models/order';
import classnames from 'classnames';
import moment from 'moment';
import { Link } from 'react-router-dom';
import { AccentBackground } from 'scy/Background';
import { PrimaryButton, SecondaryButton, TextButton } from 'scy/buttons';
import { Checkbox } from 'scy/Input';
import LoadingSpinner from 'scy/LoadingSpinner';
import { Warning } from 'scy/Warning';
import Header from './Header';
import Nav from './WizardNav';
import ProgressIndicator from './ProgressIndicator';
import ThankYouModal from './ThankYouModal';
import ContactModal from 'components/ContactModal';
import EditModal from './EditModal';
import Logo from 'images/icons/logos/combo-drop-pin.svg?react-svg';
import Pin from 'images/icons/pin-marker.svg?react-svg';
import Pencil from 'images/icons/pencil.svg?react-svg';
import Clock from 'images/icons/clock.svg?react-svg';
import Conversation from 'images/icons/conversation.svg?react-svg';
import Close from 'images/icons/controls/close.svg?react-svg';
import { width100, textCenter, textLeft, right } from 'scy/utils.mod.scss';
import { Body, H3, H5 } from 'scy/text';
import { fontSizeXS, fontSizeS, lineHeightS } from 'scy/text/styles.mod.scss';
import { flex, flex1 } from 'scy/flex.mod.scss';
import { hidden, tablet_block, tablet_hidden } from 'scy/display.mod.scss';
import { relative, absolute, top_sm, right_sm } from 'scy/position.mod.scss';
import padding from 'scy/padding.mod.scss';
import margin from 'scy/margin.mod.scss';
import styles from './ConfirmPage.mod.scss';

export default function ConfirmPage({ whenPath, history, landingPagePath }) {
  const dispatch = useDispatch();
  const { order, date, slot, selectedETA, contactNumber } = useSelector(
    state => state.giftClaim
  );

  const [isShowingThankYouModal, setIsShowingThankYouModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isShowingContactModal, setIsShowingContactModal] = useState(false);
  const [isShowingEditModal, setIsShowingEditModal] = useState(false);

  const customer = order.customer || {};
  const firstName = customer.first_name || '';
  const addressDetails = order.delivery_address_detail || {};

  const scheduledDescription =
    date && date.date && slot && slot.description
      ? `${moment(date.date).format('ddd MMM Do')}, ${slot.description}`
      : '';

  const etaDescription = selectedETA
    ? `ASAP (Est. ${selectedETA.replace('minutes', 'min')})`
    : 'ASAP';

  const canEdit = !isScheduledOrder(order);

  function handleSubmit(e) {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    return dispatch(confirmOrder())
      .then(() => {
        setIsLoading(false);
        history.push(landingPagePath);
        dispatch(openDownloadModal());
      })
      .catch(({ message }) => {
        const error = message || 'Something went wrong. Please try again.';
        setIsLoading(false);
        setError(error);
      });
  }

  return (
    <>
      <Header className={classnames(hidden, tablet_block)} />

      <Nav
        prevPath={canEdit ? whenPath : landingPagePath}
        className={classnames(hidden, tablet_block)}
      >
        <ProgressIndicator
          steps={
            canEdit
              ? { where: 'completed', when: 'completed', confirm: 'active' }
              : null
          }
        />
      </Nav>

      <AccentBackground className={styles.background}>
        <Body className={classnames(textCenter, tablet_hidden)}>
          {!canEdit && (
            <Link
              to={landingPagePath}
              className={classnames(padding.xxs, absolute, top_sm, right_sm)}
            >
              <Close />
            </Link>
          )}
          <Logo />
        </Body>

        {isLoading ? (
          <Body className={classnames(padding.top_xl, textCenter)}>
            <div className={classnames(relative, padding.md)}>
              <LoadingSpinner />
            </div>
            <div className={classnames(margin.sm, margin.tablet_bottom_xl)}>
              Confirming your gift...
            </div>
          </Body>
        ) : (
          <>
            <H3 tag="h1" className={styles.heading}>
              Review your gift delivery{firstName ? ` from ${firstName}` : ''}.
            </H3>

            <Body tag="dl">
              <Row
                Icon={Pin}
                title="Delivering to"
                description={`${addressDetails.address}${
                  addressDetails.unit ? `, ${addressDetails.unit}` : ''
                }`}
                onEditClick={canEdit ? () => setIsShowingEditModal(true) : null}
              />

              {!!order.delivery_notes && (
                <Row
                  Icon={Pencil}
                  title="With notes"
                  description={order.delivery_notes}
                />
              )}

              <Row
                Icon={Clock}
                title={`Delivering${scheduledDescription ? ' on' : ''}`}
                description={
                  scheduledDescription ? scheduledDescription : etaDescription
                }
                editLink={canEdit ? whenPath : null}
              />

              {canEdit ? (
                <Form
                  onSubmit={handleSubmit}
                  thankYouNote={order.recipient_note}
                  showThankYouModal={() => setIsShowingThankYouModal(true)}
                  clearThankYouNote={() => setThankYouNote(null)}
                  placeholder={`Send a Thank You${
                    firstName ? ` to ${firstName}` : ''
                  }`}
                  error={error}
                />
              ) : (
                !!order.recipient_note && (
                  <Row
                    Icon={Conversation}
                    title="Thank You"
                    description={`“${order.recipient_note}”`}
                  />
                )
              )}
            </Body>

            {!canEdit && (
              <section className={textCenter}>
                <H5 marginClass={margin.xs}>Need help?</H5>
                <SecondaryButton
                  inverted
                  onClick={() => setIsShowingContactModal(true)}
                >
                  Contact Us
                </SecondaryButton>
              </section>
            )}
          </>
        )}
      </AccentBackground>

      {isShowingThankYouModal && (
        <ThankYouModal
          isOpen={isShowingThankYouModal}
          handleClose={() => setIsShowingThankYouModal(false)}
          defaultValue={order.recipient_note}
          handleSubmit={note => {
            dispatch(setThankYouNote(note));
            setIsShowingThankYouModal(false);
          }}
        />
      )}

      {isShowingContactModal && (
        <ContactModal
          isOpen={isShowingContactModal}
          handleClose={() => setIsShowingContactModal(false)}
          phoneNumber={contactNumber}
        />
      )}

      {canEdit && (
        <EditModal
          order={order}
          isOpen={isShowingEditModal}
          handleClose={() => setIsShowingEditModal(false)}
          updateNotes={notes => dispatch(updateDeliveryNotes(notes))}
          updateUnit={unit => dispatch(updateUnit(unit))}
        />
      )}
    </>
  );
}

function Form({
  onSubmit,
  thankYouNote,
  showThankYouModal,
  clearThankYouNote,
  placeholder,
  error
}) {
  return (
    <form onSubmit={onSubmit}>
      <RowContainer as="label" className={fontSizeS}>
        <RowIcon>
          <Checkbox
            className={styles.checkbox}
            checked={!!thankYouNote && !!thankYouNote.length}
            onChange={e =>
              e.target.checked ? showThankYouModal() : clearThankYouNote()
            }
          />
        </RowIcon>
        <RowContent>
          {thankYouNote ? (
            <TextButton
              noUnderline
              type="button"
              className={textLeft}
              onClick={showThankYouModal}
            >
              <RowTitle>Thank You</RowTitle>
              <RowDescription>“{thankYouNote}”</RowDescription>
            </TextButton>
          ) : (
            placeholder
          )}
        </RowContent>
      </RowContainer>

      <div className={padding.md}>
        <PrimaryButton inverted className={width100}>
          Confirm Delivery
        </PrimaryButton>
      </div>

      {!!error && <Warning>{error}</Warning>}
    </form>
  );
}

function Row({ Icon, editLink, onEditClick, title, description }) {
  return (
    <RowContainer className={margin.bottom_md}>
      <RowIcon>
        <Icon />
      </RowIcon>
      <RowContent
        className={onEditClick ? classnames(fontSizeXS, lineHeightS) : null}
      >
        {editLink ? (
          <Link
            to={editLink}
            className={classnames(
              fontSizeXS,
              lineHeightS,
              right,
              padding.left_sm,
              padding.right_sm
            )}
          >
            Edit
          </Link>
        ) : onEditClick ? (
          <TextButton
            noUnderline
            className={classnames(right, padding.left_sm, padding.right_sm)}
            onClick={onEditClick}
          >
            Edit
          </TextButton>
        ) : null}
        <RowTitle>{title}</RowTitle>
        <RowDescription>{description}</RowDescription>
      </RowContent>
    </RowContainer>
  );
}

function RowContainer({ className, as, children }) {
  const Tag = as || 'div';

  return (
    <Tag
      className={classnames(
        flex,
        padding.left_sm,
        padding.right_sm,
        padding.tablet_left_md,
        padding.tablet_right_md,
        className
      )}
    >
      {children}
    </Tag>
  );
}

function RowIcon({ children }) {
  return <div className={styles.iconContainer}>{children}</div>;
}

function RowContent({ className, children }) {
  return <div className={classnames(flex1, className)}>{children}</div>;
}

function RowTitle({ children }) {
  return (
    <H5 tag="dt" uppercase>
      {children}
    </H5>
  );
}

function RowDescription({ children }) {
  return <dd className={styles.description}>{children}</dd>;
}
